import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, FileText, Clock, CheckCircle2, Archive } from 'lucide-react';
import ProjectCard from '@/components/ProjectCard';
import api from '@/utils/api';
import { toast } from 'sonner';

const Dashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  useEffect(() => {
    loadProjects();
  }, []);
  
  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await api.getProjects();
      setProjects(data);
    } catch (error) {
      toast.error(t('errorLoading'));
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteProject = async (id) => {
    try {
      await api.deleteProject(id);
      toast.success(t('projectDeleted'));
      loadProjects();
    } catch (error) {
      toast.error(t('errorDeleting'));
    }
  };
  
  const handleDuplicateProject = async (id, name) => {
    try {
      await api.duplicateProject(id, `${name} (Copy)`);
      toast.success(t('projectDuplicated'));
      loadProjects();
    } catch (error) {
      toast.error(t('errorDuplicating'));
    }
  };
  
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = 
      activeTab === 'all' || 
      (activeTab === 'active' && project.status === 'in_progress') ||
      (activeTab === 'draft' && project.status === 'draft') ||
      (activeTab === 'completed' && project.status === 'completed');
    return matchesSearch && matchesTab;
  });
  
  const stats = [
    {
      label: t('allProjects'),
      value: projects.length,
      icon: FileText,
      color: 'text-[#4a5568]',
      bgColor: 'bg-slate-100',
    },
    {
      label: t('draftProjects'),
      value: projects.filter(p => p.status === 'draft').length,
      icon: FileText,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
    {
      label: t('activeProjects'),
      value: projects.filter(p => p.status === 'in_progress').length,
      icon: FileText,
      color: 'text-[#1e40af]',
      bgColor: 'bg-blue-50',
    },
    {
      label: t('completedProjects'),
      value: projects.filter(p => p.status === 'completed').length,
      icon: FileText,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];
  
  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
            {t('dashboard')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {t('manageArchitecturalDocs')}
          </p>
        </div>
        
        <Button
          onClick={() => navigate('/project/new')}
          size="lg"
          className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white shadow-sm"
          data-testid="new-project-btn"
        >
          <Plus className="mr-2 h-5 w-5" />
          {t('newProject')}
        </Button>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="p-6 bg-slate-50 border border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {stat.label}
                  </p>
                  <p className="text-3xl font-bold text-[#1f2937] dark:text-white">
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      
      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>
      
      {/* Projects List */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-4">
          <TabsTrigger value="all" data-testid="tab-all">
            {t('allProjects')}
          </TabsTrigger>
          <TabsTrigger value="draft" data-testid="tab-draft">
            {t('draft')}
          </TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">
            {t('in_progress')}
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            {t('completed')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-slate-600 dark:text-slate-400">{t('loadingProjects')}</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText className="mx-auto h-16 w-16 text-slate-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                {t('noProjectsFound')}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {t('startCreatingFirst')}
              </p>
              <Button
                onClick={() => navigate('/project/new')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('newProject')}
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onDelete={handleDeleteProject}
                  onDuplicate={handleDuplicateProject}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;